import { computed } from 'vue'
import { useProjectStore } from '../stores/projects.js'
import { getDeadlineInfo, formatRemaining, effectiveDeadline } from '../utils/dateUtils.js'

// Livelli considerati "a rischio" (mostrati nel segnale globale)
const ALERT_LEVELS = ['warn', 'critical', 'overdue']

// Ordine di urgenza dei livelli (più alto = più urgente)
const LEVEL_RANK = { none: 0, safe: 1, warn: 2, critical: 3, overdue: 4 }

// Vero se `a` è più urgente di `b` (livello peggiore, a parità la scadenza più vicina).
function isMoreUrgent(a, b) {
  const ra = LEVEL_RANK[a.level] ?? 0
  const rb = LEVEL_RANK[b.level] ?? 0
  if (ra !== rb) return ra > rb
  if (a.msRemaining == null) return false
  if (b.msRemaining == null) return true
  return a.msRemaining < b.msRemaining
}

// Normalizza la configurazione deadline di uno status nel formato { field, doneField, warnHours, criticalHours }.
// Supporta sia il nuovo array `deadlines` sia il vecchio campo singolo `deadlineField`.
function deadlineConfigsOf(statusInfo) {
  if (!statusInfo) return []
  if (Array.isArray(statusInfo.deadlines) && statusInfo.deadlines.length > 0) {
    return statusInfo.deadlines
  }
  if (statusInfo.deadlineField) {
    return [{
      field: statusInfo.deadlineField,
      doneField: statusInfo.deadlineDoneField,
      warnHours: statusInfo.deadlineWarnHours,
      criticalHours: statusInfo.deadlineCriticalHours
    }]
  }
  return []
}

/** Colore Vuetify associato al livello di urgenza. */
export function deadlineColor(level) {
  switch (level) {
    case 'overdue':
    case 'critical':
      return 'red'
    case 'warn':
      return 'orange'
    case 'safe':
      return 'green'
    default:
      return 'grey'
  }
}

/**
 * Composable condiviso per le deadline.
 * - `projectDeadline(project)`: calcola lo stato deadline di un progetto in tabella,
 *   leggendo la configurazione dallo status corrente (cfg.json).
 * - `deadlineAlerts`: lista (reattiva) dei progetti a rischio dal segnale globale.
 */
export function useDeadlines() {
  const projectStore = useProjectStore()

  // Stato deadline di un progetto (riga tabella), in base allo status corrente.
  // Se lo status ha più deadline, mostra la più urgente.
  const projectDeadline = (project) => {
    if (!project) return { level: 'none', msRemaining: null }
    const statusInfo = projectStore.getStatusInfo(
      String(project.activeStatus),
      project.projectType
    )
    const configs = deadlineConfigsOf(statusInfo)
    if (configs.length === 0) return { level: 'none', msRemaining: null }

    let worst = { level: 'none', msRemaining: null }
    for (const c of configs) {
      // For relative deadlines (offsetDays) the instant is the field value + offset.
      const deadlineVal = effectiveDeadline(project[c.field], c.offsetDays)
      const done = c.doneField ? !!project[c.doneField] : false
      const info = getDeadlineInfo(
        deadlineVal,
        { warnHours: c.warnHours, criticalHours: c.criticalHours },
        done
      )
      if (isMoreUrgent(info, worst)) worst = { ...info, deadline: deadlineVal }
    }
    return worst
  }

  // Progetti a rischio dal segnale globale (endpoint dedicato), ordinati per urgenza.
  const deadlineAlerts = computed(() => {
    return (projectStore.deadlines || [])
      .map(d => {
        const info = getDeadlineInfo(
          d.deadline,
          { warnHours: d.warnHours, criticalHours: d.criticalHours },
          d.done
        )
        return { ...d, ...info }
      })
      .filter(d => ALERT_LEVELS.includes(d.level))
      .sort((a, b) => (a.msRemaining ?? Infinity) - (b.msRemaining ?? Infinity))
  })

  // Livello peggiore presente fra gli alert (per il colore del badge).
  const worstAlertLevel = computed(() => {
    const levels = deadlineAlerts.value.map(d => d.level)
    if (levels.includes('overdue') || levels.includes('critical')) return 'critical'
    if (levels.includes('warn')) return 'warn'
    return 'none'
  })

  return {
    projectDeadline,
    deadlineAlerts,
    worstAlertLevel,
    deadlineColor,
    formatRemaining
  }
}
